import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class GridUtilsService {
  constructor() {}

  /**
   * Converts a string to sentence case, replacing underscores with spaces
   * and capitalizing the first letter of each word.
   * @param str - The string to be converted.
   * @returns The sentence-cased version of the string.
   */
  toSentenceCase(str: string): string {
    return str
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/(^\w|\s\w)/g, (match) => match.toUpperCase());
  }

  /**
   * Checks if a value is numeric, allowing both number and numeric string types.
   * @param value - The value to check.
   * @returns True if the value is a number or numeric string, otherwise false.
   */
  isNumber(value: any): boolean {
    if (typeof value === 'number') {
      return true;
    }
    if (typeof value === 'string') {
      return !isNaN(Number(value)) && value.trim() !== '';
    }
    return false;
  }

  /**
   * Checks if a value is a decimal (non-integer) number.
   * @param value - The value to check.
   * @returns True if the value is a decimal, otherwise false.
   */
  isDecimal(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value) && value % 1 !== 0;
  }

  isObject(value: any): boolean {
    return typeof value === 'object';
  }

  isString(value: any): boolean {
    return typeof value === 'string';
  }

  /**
   * Extracts a date object from the parameter value, supporting multiple date formats.
   * @param value - The raw value from which to derive a date.
   * @returns A Date object if a valid date is present, otherwise null.
   */
  extractDateFromParams(value: any): Date | null {
    if (!value) return null;

    if (value instanceof Date) {
      return value;
    } else if (this.isObject(value) && value['^t']) {
      return new Date(value['^t'] * 1000);
    } else if (this.isObject(value) && value['^O'] === 'Date') {
      return new Date(value.year, value.month - 1, value.day);
    }
    if (this.isString(value)) {
      // Strict regex pattern to check valid ISO-8601 date or common formats
      const dateRegex =
        /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])(?:[T\s](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?(?:Z|([+-](?:[01]\d|2[0-3]):?[0-5]\d))?)?$/;

      if (dateRegex.test(value)) {
        const parsedDate = new Date(value);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      }
    }
    return null;
  }
}
